<template>
    <!-- spec 2026-10-07 §14.4 R29: the order detail INLINE under its row (no redirect).
         Info blocks come from the list row (it already has every bo_orders column);
         order_items / wms_items come from the lazy GET /orders/detail (`detail`).
         §15.3 round 7: no "hide images" switch (R32); "hide details" sits at the
         bottom right (R33); the slip opens the page-level preview (R34); status
         100 + isAccessPayment gets the payment-check buttons under it (R35). -->
    <div class="bon-detail" data-testid="order-detail">
        <!-- Header: status · #ID · platform/channel · created · staff (info only, §15.3 R33) -->
        <div class="d-flex flex-wrap align-items-center bon-detail-head">
            <span class="badge bon-badge-lg mr-2" :class="badgeClass">{{ statusText }}</span>
            <!-- Shopee cancel reconcile §11 F1 (same helper as the table cell) -->
            <span v-if="statusExtras.cancelRequested" class="badge badge-warning mr-2" data-testid="detail-cancel-requested">{{ $t("orders_new.cancel_requested") }}</span>
            <span
                v-if="statusExtras.staleChip"
                class="badge badge-secondary mr-2"
                data-testid="detail-stale-chip"
                :title="$t('orders_new.platform_status_stale')"
            >{{ statusExtras.staleChip }}</span>
            <span class="font-weight-bold mr-2">#{{ order.ID }}</span>
            <span class="text-muted bon-detail-meta mr-3">
                {{ platformText }}<template v-if="channelText"> / {{ channelText }}</template>
                · {{ $t("created") }} {{ order.created_at || "-" }}
                <template v-if="order.bo_order_user_name">· {{ $t("orders_new.created_by") }} {{ order.bo_order_user_name }}</template>
            </span>
        </div>

        <!-- Spec 2026-10-14 §3.2 #8: the last tracking failure (additive field). -->
        <div v-if="trackingFailure" class="bon-tf d-flex flex-wrap align-items-center" role="alert" data-testid="detail-tracking-failure">
            <i class="fa fa-exclamation-circle mr-2"></i>
            <span class="mr-2">
                {{ $t("tracking_failure.detail_line", { reason: $t(trackingFailureReasonKey) }) }}
                <template v-if="trackingFailure.last_failed_at"> · {{ failureTime }}</template>
            </span>
            <span v-if="trackingFailure.carrier_message" class="bon-tf-msg mr-2">({{ trackingFailure.carrier_message }})</span>
            <base-button
                v-if="canEditRecipient"
                size="sm"
                type="danger"
                outline
                class="ml-auto"
                data-testid="detail-edit-recipient"
                @click="$emit('edit-recipient', order)"
            ><i class="fa fa-address-card mr-1"></i>{{ $t("tracking_failure.edit_recipient") }}</base-button>
        </div>

        <div class="row">
            <!-- ============ Left: info + lists ============ -->
            <div :class="layout.main">
                <div class="row">
                    <div class="col-12 col-md-4 mb-3">
                        <div class="bon-block">
                            <div class="bon-block-title">{{ $t("orders_new.recipient") }}</div>
                            <div class="font-weight-bold">{{ order.recipient_name || "-" }}</div>
                            <div>{{ order.recipient_phone || "-" }}</div>
                            <div class="bon-wrap">{{ addressText || "-" }}</div>
                        </div>
                    </div>
                    <div class="col-12 col-md-4 mb-3">
                        <div class="bon-block">
                            <div class="bon-block-title">{{ $t("orders_new.shipping") }}</div>
                            <div><span class="bon-k">{{ $t("shipping_provider") }}:</span> {{ order.shipping_provider_name || "-" }}</div>
                            <div class="bon-wrap">
                                <span class="bon-k">{{ $t("tracking_number") }}:</span> {{ order.shipping_tracking_number || "-" }}
                                <button
                                    v-if="clipboardText(order.shipping_tracking_number)"
                                    type="button"
                                    class="btn btn-link p-0 ml-1 bon-copy"
                                    data-testid="detail-copy-tracking"
                                    :title="$t('orders_new.copy')"
                                    :aria-label="$t('orders_new.copy')"
                                    @click.stop="copy(order.shipping_tracking_number)"
                                ><i class="fa fa-copy"></i></button>
                                <!-- §20 R47: carrier timeline. Only with a tracking number; the modal itself
                                     lives in OrdersNew.vue (page level — this panel is rendered twice by
                                     el-table's fixed-right copy and sits inside the expanded row). -->
                                <button
                                    v-if="hasTracking"
                                    type="button"
                                    class="btn btn-sm btn-outline-primary bon-track-btn"
                                    data-testid="detail-tracking-timeline"
                                    @click.stop="$emit('tracking', order)"
                                ><i class="fa fa-route mr-1"></i>{{ $t("orders_new.tracking_view") }}</button>
                                <!-- bo-api #931: admins set / change the number. Same gate as the ⋮
                                     "tracking" item; OrdersNew.vue hands it to the orders page dialog. -->
                                <button
                                    v-if="canChangeTracking"
                                    type="button"
                                    class="btn btn-sm btn-outline-warning bon-track-btn"
                                    data-testid="detail-change-tracking"
                                    @click.stop="$emit('change-tracking', row)"
                                ><i class="fa fa-truck mr-1"></i>{{ $t("tracking_change.action") }}</button>
                            </div>
                            <div v-if="order.bo_shipping_user_name"><i class="fa fa-user mr-1 bon-k"></i>{{ order.bo_shipping_user_name }}</div>
                            <!-- §19 R44: packing open/close (same fields as the old page OrderTables.vue); "-" when empty. -->
                            <div class="bon-packing" data-testid="detail-packing">
                                <div data-testid="detail-packing-opened-by"><span class="bon-k">{{ $t("orders_new.pk_opened_by") }}:</span> {{ packing.openedBy }}</div>
                                <div data-testid="detail-packing-opened-at"><span class="bon-k">{{ $t("orders_new.pk_opened_at") }}:</span> {{ packing.openedAt }}</div>
                                <div data-testid="detail-packing-closed-at"><span class="bon-k">{{ $t("orders_new.pk_closed_at") }}:</span> {{ packing.closedAt }}</div>
                                <div data-testid="detail-packing-line"><span class="bon-k">{{ $t("orders_new.pk_line") }}:</span> {{ packing.line }}</div>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-md-4 mb-3">
                        <div class="bon-block">
                            <div class="bon-block-title">{{ $t("orders_new.payment") }}</div>
                            <div><span class="bon-k">{{ $t("payment_method") }}:</span> {{ paymentMethodText || "-" }}</div>
                            <div><span class="bon-k">{{ $t("payment_at") }}:</span> {{ order.payment_at || "-" }}</div>
                            <div v-if="order.bo_payment_user_name"><i class="fa fa-user mr-1 bon-k"></i>{{ order.bo_payment_user_name }}</div>
                        </div>
                    </div>
                </div>

                <!-- Totals: one line -->
                <div class="bon-totals mb-3" data-testid="detail-totals">
                    <span>{{ $t("orders_new.amount_items") }} {{ money(order.amount) }}</span>
                    <span class="bon-dot">·</span>
                    <span>{{ $t("discount") }} {{ money(order.discount) }}</span>
                    <span class="bon-dot">·</span>
                    <span>{{ $t("orders_new.shipping_cost") }} {{ money(order.shipping_cost) }}</span>
                    <span class="bon-dot">·</span>
                    <span class="font-weight-bold">{{ $t("orders_new.amount_pay") }} {{ money(order.pay_amount) }}</span>
                </div>

                <div v-if="order.seller_note" class="mb-2 bon-wrap">
                    <span class="bon-k">{{ $t("seller_note") }}:</span> {{ order.seller_note }}
                </div>
                <div v-if="isCancelled && order.cancel_reason" class="mb-2 bon-wrap text-danger">
                    <span class="bon-k">{{ $t("orders_new.cancel_reason") }}:</span> {{ order.cancel_reason }}
                    <template v-if="order.bo_cancel_user_name"> ({{ order.bo_cancel_user_name }})</template>
                </div>

                <!-- Load error: message + retry (R28) -->
                <div v-if="error && !loading" class="alert alert-secondary d-flex align-items-center mb-3" data-testid="detail-error">
                    <span class="mr-3">{{ error }}</span>
                    <base-button size="sm" type="primary" class="ml-auto" @click="$emit('retry')">
                        <i class="fa fa-redo mr-1"></i>{{ $t("orders_new.detail_retry") }}
                    </base-button>
                </div>

                <!-- ============ Items ============ -->
                <div class="bon-section">
                    <div class="bon-section-title">
                        {{ $t("orders_new.items") }}<template v-if="!loading && !error"> ({{ items.length }})</template>
                    </div>
                    <template v-if="loading">
                        <div v-for="n in 3" :key="'si' + n" class="bon-line">
                            <div class="bon-thumb bon-skel"></div>
                            <div class="flex-grow-1">
                                <div class="bon-skel bon-skel-text w-50"></div>
                                <div class="bon-skel bon-skel-text w-25 mt-1"></div>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="!error">
                        <div v-if="!items.length" class="text-muted py-2">{{ $t("orders_new.no_items") }}</div>
                        <div v-for="(it, i) in items" :key="'it' + i" class="bon-line" data-testid="detail-item">
                            <img
                                v-if="showImg(productImage(it))"
                                :src="productImage(it)"
                                :alt="it.bo_product_name"
                                loading="lazy"
                                class="bon-thumb"
                                @error="onImgError(productImage(it))"
                            />
                            <div v-else class="bon-thumb bon-thumb-empty"><i class="fa fa-image"></i></div>
                            <div class="flex-grow-1 bon-line-text">
                                <div class="font-weight-bold bon-wrap">{{ it.bo_product_name || "-" }}</div>
                                <small v-if="it.bo_product_sku_name || it.sku_code" class="text-muted d-block bon-wrap">
                                    {{ it.bo_product_sku_name }}<template v-if="it.bo_product_sku_name && it.sku_code"> · </template>{{ it.sku_code }}
                                </small>
                            </div>
                            <div class="text-right bon-qty">
                                <div class="font-weight-bold">× {{ it.quantity }}</div>
                                <small class="text-muted">{{ money(it.price_per_unit) }}</small>
                            </div>
                        </div>
                    </template>
                </div>

                <!-- ============ WMS pack list ============ -->
                <div class="bon-section">
                    <div class="bon-section-title">
                        {{ $t("orders_new.wms_items") }}<template v-if="!loading && !error"> ({{ wmsItems.length }})</template>
                    </div>
                    <template v-if="loading">
                        <div v-for="n in 3" :key="'sw' + n" class="bon-line">
                            <div class="bon-thumb bon-skel"></div>
                            <div class="flex-grow-1">
                                <div class="bon-skel bon-skel-text w-50"></div>
                                <div class="bon-skel bon-skel-text w-25 mt-1"></div>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="!error">
                        <div v-if="!wmsItems.length" class="text-muted py-2">{{ $t("orders_new.no_wms_items") }}</div>
                        <div v-for="(w, i) in wmsItems" :key="'w' + i" class="bon-line" data-testid="detail-wms-item">
                            <img
                                v-if="showImg(wmsImage(w))"
                                :src="wmsImage(w)"
                                :alt="w.wms_product_name"
                                loading="lazy"
                                class="bon-thumb"
                                @error="onImgError(wmsImage(w))"
                            />
                            <div v-else class="bon-thumb bon-thumb-empty"><i class="fa fa-image"></i></div>
                            <div class="flex-grow-1 bon-line-text">
                                <div class="font-weight-bold bon-wrap">{{ w.wms_product_name || "-" }}</div>
                                <small v-if="w.barcode" class="text-muted d-block">{{ w.barcode }}</small>
                            </div>
                            <div class="text-right bon-qty">
                                <div class="font-weight-bold">× {{ w.pickup_quantity }}</div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>

            <!-- ============ Right: slip and/or payment check (§15.3 R34/R35) ============ -->
            <div v-if="showRight" :class="layout.slip" data-testid="detail-slip">
                <div class="bon-block">
                    <div class="bon-block-title">{{ hasSlip ? $t("orders_new.payment_proof") : $t("orders_new.payment_check") }}</div>
                    <template v-if="hasSlip">
                        <!-- R34: a button, not a link — the preview opens in this page (OrdersNew.vue). -->
                        <button
                            v-if="!broken[slipUrl]"
                            type="button"
                            class="bon-slip-btn p-0 border-0 bg-transparent"
                            data-testid="detail-slip-preview"
                            :title="$t('orders_new.preview_slip')"
                            :aria-label="$t('orders_new.preview_slip')"
                            @click="$emit('preview', slipUrl)"
                        >
                            <img :src="slipUrl" :alt="$t('orders_new.payment_proof')" loading="lazy" class="bon-slip" @error="onImgError(slipUrl)" />
                        </button>
                        <div v-else class="bon-slip-empty"><i class="fa fa-image"></i></div>
                    </template>
                    <div v-else class="text-muted" data-testid="detail-no-slip">{{ $t("orders_new.no_slip") }}</div>
                    <!-- R35: two equal-width buttons under the slip; OrdersNew.vue confirms and calls the API. -->
                    <div v-if="canCheckPayment" class="bon-pay-actions mt-3" data-testid="detail-payment-actions">
                        <button
                            type="button"
                            class="btn btn-primary"
                            data-testid="detail-payment-ok"
                            :disabled="paymentBusy"
                            @click="$emit('payment-check', 'success')"
                        >
                            <i v-if="paymentBusy" class="fa fa-spinner fa-spin mr-1"></i>{{ $t("payment_success") }}
                        </button>
                        <button
                            type="button"
                            class="btn btn-danger"
                            data-testid="detail-payment-fail"
                            :disabled="paymentBusy"
                            @click="$emit('payment-check', 'fail')"
                        >
                            <i v-if="paymentBusy" class="fa fa-spinner fa-spin mr-1"></i>{{ $t("payment_fail") }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- R33: "hide details" at the bottom right of the panel. -->
        <div class="d-flex justify-content-end mt-3">
            <base-button
                size="sm"
                type="secondary"
                data-testid="detail-close"
                @click="$emit('close')"
            >
                <i class="fa fa-chevron-up mr-1"></i>{{ $t("orders_new.detail_hide") }}
            </base-button>
        </div>
    </div>
</template>

<script>
// Inline order detail of the new BO order list (OrdersNew.vue) — spec 2026-10-07
// §14.4 R29, §15.3 R32–R35. Stateless: OrdersNew.vue owns the fetch, the cache,
// the slip preview overlay and the payment-check calls; this panel only renders
// and emits `close`, `retry`, `preview(src)`, `payment-check('success'|'fail')`,
// `tracking(row)` (§20 R47: OrdersNew.vue opens the timeline modal).
import { STATUS_LABEL_KEYS, orderStatusExtras, rowActions, statusBadgeClass } from "@/utils/ordersNew";
import { clipboardText, copyToClipboard } from "@/utils/clipboard";
import { canEditRecipient, formatFailureTime, reasonKey } from "@/utils/trackingFailure";
import {
    IMAGE_PREFIX,
    canCheckPayment,
    detailLayout,
    hasRightColumn,
    imageUrl,
    orderItemsOf,
    packingInfo,
    wmsItemsOf
} from "@/utils/orderDetailPanel";

export default {
    name: "OrdersNewDetail",
    props: {
        row: { type: Object, required: true },
        detail: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        error: { type: String, default: "" },
        apiBase: { type: String, default: "" },
        // OrdersNew.vue#roleFlags (§15.3 R35: isAccessPayment; #931: isAdmin for the tracking change).
        roles: { type: Object, default: () => ({}) },
        // true while this row's payment-check request runs (both buttons disabled).
        paymentBusy: { type: Boolean, default: false },
        // Spec 2026-10-14: the viewer may edit the recipient (Auth/isFullOrder).
        canEditRecipientRole: { type: Boolean, default: false }
    },
    data() {
        return {
            // url -> true once the <img> fired `error` (then the placeholder shows).
            broken: {}
        };
    },
    computed: {
        // The list row has every bo_orders column; the detail adds order_items / wms_items.
        order() {
            return Object.assign({}, this.row || {}, this.detail || {});
        },
        items() {
            return orderItemsOf(this.detail);
        },
        wmsItems() {
            return wmsItemsOf(this.detail, this.row);
        },
        slipUrl() {
            return imageUrl(this.apiBase, IMAGE_PREFIX.slip, this.order.payment_files);
        },
        hasSlip() {
            return !!this.slipUrl;
        },
        // §20 R47: the "view tracking" button needs a tracking number (blank = none).
        hasTracking() {
            return !!clipboardText(this.order.shipping_tracking_number);
        },
        canCheckPayment() {
            return canCheckPayment(this.order, this.roles);
        },
        // #931: the ⋮ "tracking" gate (admins; TRACKING_EDIT_STATUSES).
        canChangeTracking() {
            return rowActions(this.order, this.roles).some(a => a.key === "tracking");
        },
        showRight() {
            return hasRightColumn(this.order, this.roles);
        },
        layout() {
            return detailLayout(this.showRight);
        },
        badgeClass() {
            return statusBadgeClass(this.order.order_status);
        },
        // §11 F1 (Shopee cancel reconcile): { cancelRequested, staleChip }.
        statusExtras() {
            return orderStatusExtras(this.order);
        },
        statusText() {
            const key = STATUS_LABEL_KEYS[Number(this.order.order_status)];
            return key ? this.$t(key) : String(this.order.order_status);
        },
        isCancelled() {
            return Number(this.order.order_status) >= 140;
        },
        platformText() {
            const list = (this.$store && this.$store.getters["Order/getPlatformTable"]) || [];
            const hit = list.find(x => x.id === Number(this.order.platform_id));
            return hit ? hit.name : "-";
        },
        channelText() {
            const list = (this.$store && this.$store.getters["SaleChannel/getData"]) || [];
            const hit = list.find(x => x.id === this.order.bo_sale_channel_id);
            return hit ? hit.name : "";
        },
        addressText() {
            const o = this.order;
            const full = (o.recipient_address_fulladdress || "").trim();
            const parts = full
                ? [full]
                : [o.recipient_address_detail, o.recipient_address_district, o.recipient_address_city, o.recipient_address_province]
                    .map(s => (s || "").trim())
                    .filter(Boolean);
            const zip = (o.recipient_zipcode || "").trim();
            if (zip && parts.join(" ").indexOf(zip) < 0) parts.push(zip);
            return parts.join(" ");
        },
        // §19 R44: storages = WmsStorage/getData (OrdersNew.vue loads it, like OrderTables.vue).
        packing() {
            const storages = (this.$store && this.$store.getters["WmsStorage/getData"]) || [];
            return packingInfo(this.order, storages);
        },
        trackingFailure() {
            const tf = this.order.tracking_failure;
            return tf && typeof tf === "object" && tf.reason_code ? tf : null;
        },
        failureTime() {
            return formatFailureTime(this.trackingFailure && this.trackingFailure.last_failed_at);
        },
        trackingFailureReasonKey() {
            return reasonKey(this.trackingFailure && this.trackingFailure.reason_code);
        },
        canEditRecipient() {
            return canEditRecipient(this.order) && this.canEditRecipientRole;
        },
        paymentMethodText() {
            return this.order.payment_method_name || this.order.bo_payment_method_name || "";
        }
    },
    methods: {
        productImage(item) {
            return imageUrl(this.apiBase, IMAGE_PREFIX.product, item && item.image);
        },
        wmsImage(item) {
            return imageUrl(this.apiBase, IMAGE_PREFIX.wms, item && item.image);
        },
        showImg(url) {
            return !!url && !this.broken[url];
        },
        onImgError(url) {
            if (url) this.$set(this.broken, url, true);
        },
        money(value) {
            const n = Number(value || 0);
            return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        clipboardText(value) {
            return clipboardText(value);
        },
        async copy(value) {
            const text = clipboardText(value);
            const ok = await copyToClipboard(text);
            if (ok) this.$notify({ type: "success", message: this.$t("orders_new.copied", { value: text }) });
            else this.$notify({ type: "danger", message: this.$t("orders_new.copy_failed") });
            return ok;
        }
    }
};
</script>

<style scoped>
.bon-detail {
    padding: 12px 16px;
    background: #f8f9fe;
    border-left: 3px solid #1c1026;
    color: #525f7f;
    font-size: 0.875rem;
    line-height: 1.5;
    text-align: left;
    white-space: normal;
    word-break: normal;
}
.bon-detail-head {
    margin-bottom: 12px;
}
.bon-tf {
    gap: 4px;
    margin-bottom: 12px;
    padding: 8px 12px;
    background: #fdecea;
    border: 1px solid #f5c2c0;
    border-radius: 6px;
    color: #a3271f;
    font-weight: 600;
}
.bon-tf-msg {
    font-weight: 400;
    word-break: break-word;
}
.bon-detail-meta {
    font-size: 0.8125rem;
}
.bon-block {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 10px 12px;
    height: 100%;
}
.bon-block-title,
.bon-section-title {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #6c757d;
    margin-bottom: 6px;
}
.bon-k {
    color: #6c757d;
}
.bon-packing {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px dashed #e9ecef;
}
.bon-wrap {
    overflow-wrap: anywhere;
}
.bon-totals {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 8px 12px;
}
.bon-dot {
    margin: 0 8px;
    color: #6c757d;
}
.bon-section {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 12px;
}
.bon-line {
    display: flex;
    align-items: center;
    padding: 6px 0;
    border-top: 1px solid #f1f3f9;
}
.bon-section-title + .bon-line,
.bon-section-title + div + .bon-line {
    border-top: 0;
}
.bon-thumb {
    flex: 0 0 56px;
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: 6px;
    margin-right: 12px;
    background: #e9ecef;
}
.bon-thumb-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    font-size: 1.25rem;
}
.bon-line-text {
    min-width: 0;
}
.bon-qty {
    flex: 0 0 auto;
    margin-left: 12px;
    white-space: nowrap;
}
.bon-slip-btn {
    display: block;
    margin: 0 auto;
    cursor: zoom-in;
    line-height: 0;
}
.bon-slip {
    max-width: 100%;
    max-height: 360px;
    border-radius: 6px;
    border: 1px solid #e9ecef;
}
.bon-slip-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    border-radius: 6px;
    background: #e9ecef;
    color: #6c757d;
    font-size: 2rem;
}
/* R35: two equal-width buttons side by side (theme ink: white on primary/danger). */
.bon-pay-actions {
    display: flex;
}
.bon-pay-actions .btn {
    flex: 1 1 0;
    min-width: 0;
    margin: 0;
    white-space: normal;
}
.bon-pay-actions .btn + .btn {
    margin-left: 8px;
}
.bon-copy {
    color: #525f7f;
    font-size: 0.8rem;
    vertical-align: baseline;
    line-height: 1;
}
.bon-copy:hover,
.bon-copy:focus {
    color: #1c1026;
}
/* §20 R47: small inline button after the tracking number (wraps under it on narrow panels).
   The gap after the copy icon is the theme's `.btn:not(:last-child) { margin-right: .5rem }`. */
.bon-track-btn {
    padding: 1px 8px;
    font-size: 0.75rem;
    line-height: 1.5;
    vertical-align: baseline;
    white-space: nowrap;
}
.bon-skel {
    background: linear-gradient(90deg, #e9ecef 25%, #f4f5f7 50%, #e9ecef 75%);
    background-size: 200% 100%;
    animation: bon-skel 1.2s ease-in-out infinite;
}
.bon-skel-text {
    height: 12px;
    border-radius: 4px;
}
@keyframes bon-skel {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}
</style>
